import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Trash, UploadSimple, DownloadSimple } from '@phosphor-icons/react';
import { useSession, useUser } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

interface AttributeDocument {
  id: number;
  fileName: string;
  downloadUrl: string | null;
  sharePointFileUrl: string | null;
  createdAt: string;
}

interface Props {
  /** Id van het gebouwattribuut (garage, parking, berging, ...) */
  attributeId: number;
}

/**
 * Documentenbeheer voor één gebouwattribuut: uploaden (klik of drag & drop),
 * lijst met open/download-links en verwijderen. Documenten die via een
 * attribuut-only huurcontract geüpload werden verschijnen hier ook.
 */
export const AttributeDocumentsSection: React.FC<Props> = ({ attributeId }) => {
  const { session } = useSession();
  const { user } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<AttributeDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadDocuments = useCallback(async () => {
    if (!attributeId || !session) return;
    setLoading(true);
    try {
      const resp = await fetchWithAuth(
        `${API_URL}/file/building-attribute/${attributeId}/documents`,
        { method: 'GET' },
        session,
      );
      if (!resp.ok) throw new Error(`Fout bij ophalen documenten: ${resp.status}`);
      setDocuments(await resp.json());
    } catch (err) {
      console.error('AttributeDocumentsSection loadDocuments:', err);
      toast.error('Kon documenten niet laden.');
    } finally {
      setLoading(false);
    }
  }, [attributeId, session]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const uploadFiles = async (files: FileList | File[]) => {
    if (!attributeId || !session || !user?.id) return;
    const MAX_SIZE = 25 * 1024 * 1024;
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      if (file.size > MAX_SIZE) {
        toast.error(`Bestand "${file.name}" is te groot (max 25 MB).`);
        return;
      }
    }
    setUploading(true);
    try {
      for (const file of fileArray) {
        const formData = new FormData();
        formData.append('file', file);
        const resp = await fetchWithAuth(
          `${API_URL}/file/building-attribute/${attributeId}/documents`,
          { method: 'POST', body: formData, headers: { 'x-user-id': user.id } },
          session,
        );
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err?.message || `Upload mislukt (${resp.status})`);
        }
      }
      toast.success(
        fileArray.length === 1 ? 'Document toegevoegd.' : `${fileArray.length} documenten toegevoegd.`,
      );
      await loadDocuments();
    } catch (err: any) {
      console.error('AttributeDocumentsSection upload:', err);
      toast.error(err?.message || 'Upload mislukt.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      uploadFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (doc: AttributeDocument) => {
    if (!session) return;
    setDeletingId(doc.id);
    try {
      const resp = await fetchWithAuth(
        `${API_URL}/file/building-attribute/${attributeId}/documents/${doc.id}`,
        { method: 'DELETE' },
        session,
      );
      if (!resp.ok) throw new Error(`Verwijderen mislukt (${resp.status})`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      toast.success('Document verwijderd.');
    } catch (err) {
      console.error('AttributeDocumentsSection delete:', err);
      toast.error('Kon document niet verwijderen.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (raw: string) => {
    const d = new Date(raw);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="bg-muted border-muted-foreground/20 overflow-hidden rounded-lg border">
      <div className="border-neutral-primary/10 flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-brick-primary" weight="duotone" />
          <h3 className="text-neutral-primary text-base font-semibold">
            Documenten
            {documents.length > 0 && (
              <span className="text-neutral-secondary-mid ml-2 text-sm font-normal">
                ({documents.length})
              </span>
            )}
          </h3>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="gap-1.5"
        >
          {uploading ? <Spinner className="h-3 w-3" /> : <UploadSimple size={14} />}
          Document toevoegen
        </Button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileInput} />
      </div>

      <div className="p-4">
        <div
          className={`mb-4 cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
            isDragging
              ? 'border-brick-primary bg-brick-primary/10'
              : 'border-neutral-primary/20 hover:border-neutral-primary/40'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-neutral-secondary-mid">
              <Spinner className="h-4 w-4" />
              Documenten uploaden...
            </div>
          ) : (
            <p className="text-sm text-neutral-secondary-mid">
              Sleep bestanden hier of <span className="text-brick-primary font-medium">klik om te uploaden</span>
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <Spinner className="h-5 w-5" />
          </div>
        ) : documents.length === 0 ? (
          <div className="py-6 text-center">
            <FileText size={32} className="text-neutral-primary/20 mx-auto mb-2" />
            <p className="text-sm text-neutral-secondary-mid">Nog geen documenten gekoppeld</p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-primary/10">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center gap-3 py-2.5">
                <div className="shrink-0 rounded-md bg-brick-primary/10 p-2">
                  <FileText size={16} className="text-brick-primary" weight="duotone" />
                </div>
                <div className="min-w-0 flex-1">
                  <a
                    href={doc.downloadUrl || doc.sharePointFileUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-sm font-medium text-neutral-primary hover:text-brick-primary hover:underline"
                    title={doc.fileName}
                  >
                    {doc.fileName}
                  </a>
                  <span className="text-xs text-neutral-secondary-mid">{formatDate(doc.createdAt)}</span>
                </div>
                {(doc.downloadUrl || doc.sharePointFileUrl) && (
                  <a
                    href={doc.downloadUrl || doc.sharePointFileUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 rounded-md p-1.5 text-neutral-secondary-mid transition-colors hover:bg-neutral-primary/5 hover:text-neutral-primary"
                    title="Openen"
                  >
                    <DownloadSimple size={16} />
                  </a>
                )}
                <button
                  onClick={() => handleDelete(doc)}
                  disabled={deletingId === doc.id}
                  className="shrink-0 rounded-md p-1.5 text-neutral-secondary-mid transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  title="Verwijderen"
                >
                  {deletingId === doc.id ? <Spinner className="h-4 w-4" /> : <Trash size={16} />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
